import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../../services/api';
import ClientLayout from '../../components/ClientLayout';
import LookDetailView from '../../components/LookDetailView';
import { 
  PackageOpen, 
  Loader2, 
  Shirt, 
  Sparkles, 
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Clock,
  X
} from 'lucide-react';

const SEASONS = [
  { value: '', label: 'Все сезоны' },
  { value: 'лето', label: 'Лето' },
  { value: 'зима', label: 'Зима' },
  { value: 'межсезонье', label: 'Межсезонье' },
  { value: 'круглогодичная', label: 'Круглогодичная' },
];

const ClientCapsules = () => {
  const navigate = useNavigate();
  const clientId = localStorage.getItem('clientId');
  const [capsules, setCapsules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seasonFilter, setSeasonFilter] = useState('');
  const [selectedCapsule, setSelectedCapsule] = useState(null);
  const [capsuleLoading, setCapsuleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('looks');
  const [viewingImage, setViewingImage] = useState(null);
  const [viewingLook, setViewingLook] = useState(null);

  useEffect(() => {
    if (!clientId) {
      navigate('/cabinet/login');
      return;
    }
    fetchCapsules();
  }, [clientId, seasonFilter, navigate]);

  const fetchCapsules = async () => {
    setLoading(true);
    try {
      const data = await clientAPI.getCapsules(clientId, seasonFilter || null);
      setCapsules(data);
    } catch (err) {
      console.error('Error fetching capsules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCapsule = async (capsuleId) => {
    setCapsuleLoading(true);
    try {
      const detail = await clientAPI.getCapsuleDetail(capsuleId);
      setSelectedCapsule(detail);
      setActiveTab('looks');
    } catch (err) {
      console.error('Error fetching capsule detail:', err);
    } finally {
      setCapsuleLoading(false);
    }
  };

  const isRecent = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    return (now - date) < 7 * 24 * 60 * 60 * 1000;
  };

  if (loading) {
    return (
      <ClientLayout clientId={clientId}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-xxl)' }}>
          <Loader2 size={32} style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </ClientLayout>
    );
  }

  // Capsule Detail View
  if (selectedCapsule) {
    return (
      <ClientLayout clientId={clientId}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }} data-testid="capsule-detail-view">
          {/* Back Button */}
          <button
            onClick={() => setSelectedCapsule(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-s)',
              background: 'none',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              color: 'var(--mid-gray)',
              cursor: 'pointer',
              marginBottom: 'var(--space-l)',
              padding: 0
            }}
          >
            <ArrowLeft size={16} />
            Все капсулы
          </button>

          {/* Capsule Header */}
          <div style={{
            backgroundColor: 'var(--white)',
            padding: 'var(--space-xl)',
            marginBottom: 'var(--space-xl)',
            border: '1px solid var(--soft-gray)'
          }}>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: 300,
              marginBottom: 'var(--space-xs)'
            }}>
              {selectedCapsule.name}
            </h1>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              color: 'var(--light-gray)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-m)'
            }}>
              {selectedCapsule.season}
            </p>

            {selectedCapsule.description && (
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.9375rem',
                color: 'var(--mid-gray)',
                lineHeight: 1.6,
                marginBottom: 'var(--space-m)'
              }}>
                {selectedCapsule.description}
              </p>
            )}

            {selectedCapsule.event_types?.length > 0 && (
              <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                {selectedCapsule.event_types.map((event, idx) => (
                  <span key={idx} style={{
                    padding: '4px 12px',
                    backgroundColor: 'var(--cream)',
                    fontSize: '0.8125rem',
                    fontFamily: 'var(--font-body)'
                  }}>
                    {event}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 'var(--space-s)', marginBottom: 'var(--space-l)' }}>
            <button
              onClick={() => setActiveTab('looks')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                padding: '12px 20px',
                backgroundColor: activeTab === 'looks' ? 'var(--black)' : 'var(--white)',
                color: activeTab === 'looks' ? 'var(--white)' : 'var(--charcoal)',
                border: '1px solid var(--soft-gray)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                cursor: 'pointer'
              }}
            >
              <Sparkles size={16} />
              Образы
              <span style={{
                backgroundColor: activeTab === 'looks' ? 'var(--accent-gold)' : 'var(--cream)',
                color: activeTab === 'looks' ? 'var(--black)' : 'var(--charcoal)',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '0.75rem'
              }}>
                {selectedCapsule.looks?.length || 0}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('items')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-s)',
                padding: '12px 20px',
                backgroundColor: activeTab === 'items' ? 'var(--black)' : 'var(--white)',
                color: activeTab === 'items' ? 'var(--white)' : 'var(--charcoal)',
                border: '1px solid var(--soft-gray)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                cursor: 'pointer'
              }}
            >
              <Shirt size={16} />
              Вещи
              <span style={{
                backgroundColor: activeTab === 'items' ? 'var(--accent-gold)' : 'var(--cream)',
                color: activeTab === 'items' ? 'var(--black)' : 'var(--charcoal)',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '0.75rem'
              }}>
                {selectedCapsule.wardrobe_items?.length || 0}
              </span>
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'looks' && (
            <div style={{
              backgroundColor: 'var(--white)',
              border: '1px solid var(--soft-gray)',
              padding: 'var(--space-xl)'
            }}>
              {selectedCapsule.looks?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-xxl)', color: 'var(--mid-gray)' }}>
                  <Sparkles size={32} style={{ marginBottom: 'var(--space-m)', opacity: 0.3 }} />
                  <p>В этой капсуле пока нет образов</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: 'var(--space-l)'
                }}>
                  {selectedCapsule.looks?.map((look) => (
                    <div 
                      key={look.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setViewingLook(look)}
                    >
                      <div style={{
                        height: '280px',
                        backgroundColor: 'var(--cream)',
                        overflow: 'hidden'
                      }}>
                        {look.look_images?.[0] ? (
                          <img 
                            src={look.look_images[0]} 
                            alt={look.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            <Sparkles size={32} style={{ color: 'var(--light-gray)' }} />
                          </div>
                        )}
                      </div>
                      <div style={{ padding: 'var(--space-m)' }}>
                        <h4 style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: '1rem',
                          marginBottom: 'var(--space-xs)'
                        }}>
                          {look.name}
                        </h4>
                        {look.event_types?.length > 0 && (
                          <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                            {look.event_types.slice(0, 3).map((event, idx) => (
                              <span key={idx} style={{
                                padding: '2px 8px',
                                backgroundColor: 'var(--cream)',
                                fontSize: '0.6875rem'
                              }}>
                                {event}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'items' && (
            <div style={{
              backgroundColor: 'var(--white)',
              border: '1px solid var(--soft-gray)',
              padding: 'var(--space-xl)'
            }}>
              {selectedCapsule.wardrobe_items?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-xxl)', color: 'var(--mid-gray)' }}>
                  <Shirt size={32} style={{ marginBottom: 'var(--space-m)', opacity: 0.3 }} />
                  <p>В этой капсуле пока нет вещей</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 'var(--space-m)'
                }}>
                  {selectedCapsule.wardrobe_items?.map((item) => (
                    <div 
                      key={item.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => item.image_url && setViewingImage(item.image_url)}
                    >
                      <div style={{
                        height: '140px',
                        backgroundColor: 'var(--cream)',
                        overflow: 'hidden'
                      }}>
                        {item.image_url ? (
                          <img 
                            src={item.image_url} 
                            alt={item.item_kind}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            <Shirt size={24} style={{ color: 'var(--light-gray)' }} />
                          </div>
                        )}
                      </div>
                      <p style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.75rem',
                        textAlign: 'center',
                        marginTop: 'var(--space-xs)',
                        textTransform: 'capitalize'
                      }}>
                        {item.item_kind}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Look Detail View */}
          {viewingLook && (
            <LookDetailView
              look={viewingLook}
              wardrobeItems={selectedCapsule.wardrobe_items || []}
              onClose={() => setViewingLook(null)}
              onImageClick={(img) => setViewingImage(img)}
            />
          )}

          {/* Image Viewer */}
          {viewingImage && (
            <div
              onClick={() => setViewingImage(null)}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
                cursor: 'zoom-out'
              }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); setViewingImage(null); }}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: 'white',
                  padding: '12px',
                  cursor: 'pointer',
                  borderRadius: '50%'
                }}
              >
                <X size={24} />
              </button>
              <img 
                src={viewingImage} 
                alt="" 
                style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
              />
            </div>
          )}
        </div>
      </ClientLayout>
    );
  }

  // Capsules List View
  return (
    <ClientLayout clientId={clientId}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }} data-testid="capsules-list">
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
            fontWeight: 300,
            marginBottom: 'var(--space-m)'
          }}>
            Капсулы
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.9375rem',
            color: 'var(--mid-gray)'
          }}>
            Готовые наборы вещей и образов для разных сезонов и событий
          </p>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-s)',
          marginBottom: 'var(--space-xl)',
          flexWrap: 'wrap'
        }}>
          {SEASONS.map((season) => (
            <button
              key={season.value}
              onClick={() => setSeasonFilter(season.value)}
              style={{
                padding: '10px 20px',
                backgroundColor: seasonFilter === season.value ? 'var(--black)' : 'var(--white)',
                color: seasonFilter === season.value ? 'var(--white)' : 'var(--charcoal)',
                border: '1px solid var(--soft-gray)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                cursor: 'pointer'
              }}
            >
              {season.label}
            </button>
          ))}
        </div>

        {/* Capsules Grid */}
        {capsules.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-xxl)',
            backgroundColor: 'var(--white)',
            border: '1px solid var(--soft-gray)'
          }}>
            <PackageOpen size={48} style={{ color: 'var(--light-gray)', marginBottom: 'var(--space-l)' }} />
            <p style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.25rem',
              marginBottom: 'var(--space-m)'
            }}>
              {seasonFilter ? 'Нет капсул для этого сезона' : 'Пока нет капсул'}
            </p>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--mid-gray)'
            }}>
              Ваш стилист скоро создаст для вас первую капсулу
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 'var(--space-l)'
          }}>
            {capsules.map((capsule) => (
              <div
                key={capsule.id}
                onClick={() => handleOpenCapsule(capsule.id)}
                style={{
                  backgroundColor: 'var(--white)',
                  border: '1px solid var(--soft-gray)',
                  cursor: 'pointer',
                  transition: 'box-shadow 200ms ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                data-testid={`capsule-card-${capsule.id}`}
              >
                {/* Cover Image */}
                <div style={{
                  height: '180px',
                  backgroundColor: 'var(--cream)',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {capsule.cover_image ? (
                    <img 
                      src={capsule.cover_image} 
                      alt={capsule.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PackageOpen size={40} style={{ color: 'var(--light-gray)' }} />
                    </div>
                  )}

                  {/* Recent Badge */}
                  {capsule.is_recent && (
                    <span style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      padding: '4px 10px',
                      backgroundColor: 'var(--accent-gold)',
                      color: 'var(--black)',
                      fontSize: '0.6875rem',
                      fontFamily: 'var(--font-body)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Clock size={10} />
                      Обновлено
                    </span>
                  )}
                </div>

                {/* Content */}
                <div style={{ padding: 'var(--space-l)' }}>
                  <div style={{ marginBottom: 'var(--space-m)' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      {capsule.name}
                    </h3>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      color: 'var(--light-gray)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {capsule.season}
                    </p>
                  </div>

                  {capsule.description && (
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                      color: 'var(--mid-gray)',
                      marginBottom: 'var(--space-m)',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {capsule.description}
                    </p>
                  )}

                  {/* Event Tags */}
                  {capsule.event_types?.length > 0 && (
                    <div style={{
                      display: 'flex',
                      gap: 'var(--space-xs)',
                      flexWrap: 'wrap',
                      marginBottom: 'var(--space-m)'
                    }}>
                      {capsule.event_types.slice(0, 3).map((event, idx) => (
                        <span key={idx} style={{
                          padding: '2px 8px',
                          backgroundColor: 'var(--cream)',
                          fontSize: '0.6875rem',
                          fontFamily: 'var(--font-body)'
                        }}>
                          {event}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Stats */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: 'var(--space-m)',
                    borderTop: '1px solid var(--soft-gray)'
                  }}>
                    <div style={{ display: 'flex', gap: 'var(--space-l)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                        <Sparkles size={14} style={{ color: 'var(--light-gray)' }} />
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem' }}>
                          {capsule.looks_count}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                        <Shirt size={14} style={{ color: 'var(--light-gray)' }} />
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem' }}>
                          {capsule.items_count}
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={16} style={{ color: 'var(--light-gray)' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading overlay for capsule detail */}
        {capsuleLoading && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <Loader2 size={32} style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} />
          </div>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </ClientLayout>
  );
};


// Look Detail View Component for Client
export default ClientCapsules;
